import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import {
  CrewPilotNavList,
  CrewPilotNavRow,
  CrewPilotNavSectionHeader,
} from "../CrewPilotNavRow";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { SignOutButton } from "../SignOutButton";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { DesktopNavProvider, useDesktopNav } from "../navigation/DesktopNavContext";
import "../admin/adminDesktop.css";
import { usePermissions } from "../../hooks/usePermissions";
import { useUserPreferences } from "../../context/UserPreferencesContext";
import { useHardwareBackHandler } from "../../navigation/HardwareBackContext";
import { canAccessAdminSection } from "../../helpers/adminSectionAccess";
import {
  getVisibleSettingsAdminSections,
  groupSettingsAdminSectionsByCategory,
  SETTINGS_CATEGORY_ORDER,
} from "../../helpers/settingsSectionAccess";
import { isConnectedServicesUiEnabled } from "../../connectedServices/connectedServicesGate";
import { AppearanceSettingsSection } from "./AppearanceSettingsSection";
import { AshleyVoiceSettingsSection } from "./AshleyVoiceSettingsSection";
import { ConnectedServicesSection } from "./ConnectedServicesSection";
import { AboutLegalSection } from "./AboutLegalSection";
import { AdminOrganizationScreen } from "../admin/AdminOrganizationScreen";
import { AdminBrandingScreen } from "../admin/AdminBrandingScreen";
import { AdminFeatureFlagsScreen } from "../admin/AdminFeatureFlagsScreen";
import { AdminSubscriptionScreen } from "../admin/AdminSubscriptionScreen";
import { AdminUsersHubScreen } from "../admin/AdminUsersHubScreen";
import { AdminInvitationsScreen } from "../admin/AdminInvitationsScreen";
import { AdminTradesScreen } from "../admin/AdminTradesScreen";
import { AdminClassificationsScreen } from "../admin/AdminClassificationsScreen";
import { AdminPayrollTypesScreen } from "../admin/AdminPayrollTypesScreen";
import { AdminPayrollSettingsScreen } from "../admin/AdminPayrollSettingsScreen";
import { AdminWorkScheduleScreen } from "../admin/AdminWorkScheduleScreen";
import { AdminPayrollRatesScreen } from "../admin/AdminPayrollRatesScreen";
import { AdminWorkTypesScreen } from "../admin/AdminWorkTypesScreen";
import { AdminInvoicesSettingsScreen } from "../admin/AdminInvoicesSettingsScreen";
import { requestWhatsNew } from "../../whatsNew/whatsNewStore";
import "./UserPreferencesScreen.css";

const PERSONAL_SECTIONS = [
  { id: "my_appearance", label: "Appearance", icon: "🌓" },
  { id: "my_ashley", label: "Ashley", icon: "🎙️" },
  { id: "my_connected", label: "Connected Services", icon: "🔗" },
  { id: "my_whatsnew", label: "What\u2019s New", icon: "✨", action: true },
  { id: "my_legal", label: "About & Legal", icon: "ℹ️" },
];

function isPersonalSectionId(sectionId) {
  return PERSONAL_SECTIONS.some((section) => section.id === sectionId);
}

/**
 * Unified, role/capability-filtered Settings hub.
 *
 * Composes the existing personal-preference sections (previously
 * UserPreferencesScreen) with the existing Administration section registry
 * and leaf components (AdminOrganizationScreen, AdminBrandingScreen, etc.).
 * It reuses the proven capability-filtering helpers
 * (getVisibleAdminSections/canAccessAdminSection) rather than reimplementing
 * authorization — see src/helpers/settingsSectionAccess.js.
 */
export function SettingsHubScreen({
  onBack,
  currentUser,
  onSignOut,
  initialSection = "overview",
}) {
  const { can } = usePermissions();
  const { preferences, updatePreferences, error } = useUserPreferences();
  const [activeSection, setActiveSection] = useState(initialSection);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const isDesktop = useIsDesktopViewport();
  const outerNav = useDesktopNav();
  const settingsNav = useMemo(
    () => ({ ...(outerNav || {}), parentLabel: "Settings" }),
    [outerNav],
  );

  const connectedServicesEnabled = isConnectedServicesUiEnabled();

  const visiblePersonalSections = useMemo(
    () =>
      PERSONAL_SECTIONS.filter(
        (section) => section.id !== "my_connected" || connectedServicesEnabled,
      ),
    [connectedServicesEnabled],
  );

  const visibleAdminSections = useMemo(
    () => getVisibleSettingsAdminSections(can),
    [can],
  );

  const groupedAdminSections = useMemo(
    () => groupSettingsAdminSectionsByCategory(visibleAdminSections),
    [visibleAdminSections],
  );

  const canOpenAdminSection = useCallback(
    (sectionId) =>
      canAccessAdminSection(can, sectionId) &&
      visibleAdminSections.some((section) => section.id === sectionId),
    [can, visibleAdminSections],
  );

  useEffect(() => {
    if (activeSection === "overview" || isPersonalSectionId(activeSection)) {
      return;
    }
    if (!canOpenAdminSection(activeSection)) {
      setActiveSection("overview");
    }
  }, [activeSection, canOpenAdminSection]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSection]);

  const handleSelectSection = (sectionId) => {
    if (sectionId === "my_whatsnew") {
      // Opens the one existing What's New experience (always, regardless of
      // the automatic-launch preference).
      requestWhatsNew();
      return;
    }
    if (isPersonalSectionId(sectionId)) {
      setActiveSection(sectionId);
      return;
    }
    if (!canOpenAdminSection(sectionId)) {
      return;
    }
    setActiveSection(sectionId);
  };

  const handleBackToOverview = () => setActiveSection("overview");

  const handleHardwareBack = useCallback(() => {
    // Desktop personal categories share one Settings workspace, so browser
    // Back leaves it. Mobile rows and organization sections return to the
    // Settings list first.
    if (
      activeSection !== "overview" &&
      !(isDesktop && isPersonalSectionId(activeSection))
    ) {
      setActiveSection("overview");
      return true;
    }
    return false;
  }, [activeSection, isDesktop]);

  useHardwareBackHandler(handleHardwareBack);

  const handleUpdatePreferences = async (patch) => {
    setSaving(true);
    try {
      await updatePreferences(patch);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const savedBanner = saved ? (
    <p className="admin-save-confirmation" role="status">
      Preferences updated.
    </p>
  ) : null;

  if (
    isDesktop &&
    (activeSection === "overview" || isPersonalSectionId(activeSection))
  ) {
    const personalActive =
      activeSection === "my_connected" && !connectedServicesEnabled
        ? "my_appearance"
        : activeSection === "overview"
          ? "my_appearance"
          : activeSection;
    const personalMeta = PERSONAL_SECTIONS.find((section) => section.id === personalActive);
    let personalPanel = null;
    if (personalActive === "my_appearance") {
      personalPanel = (
        <AppearanceSettingsSection
          preferences={preferences}
          onUpdatePreferences={handleUpdatePreferences}
          saving={saving}
        />
      );
    } else if (personalActive === "my_ashley") {
      personalPanel = (
        <AshleyVoiceSettingsSection
          preferences={preferences}
          onUpdatePreferences={handleUpdatePreferences}
          saving={saving}
        />
      );
    } else if (personalActive === "my_connected") {
      personalPanel = <ConnectedServicesSection />;
    } else if (personalActive === "my_legal") {
      personalPanel = <AboutLegalSection />;
    }

    const navButton = (section, isOn) => (
      <button
        key={section.id}
        type="button"
        className={`adm-settings__navbtn${isOn ? " is-on" : ""}`}
        aria-current={isOn ? "page" : undefined}
        onClick={() => handleSelectSection(section.id)}
      >
        <span aria-hidden="true">{section.icon}</span>
        {section.label}
      </button>
    );

    return (
      <CrewPilotModuleShell
        title="Settings"
        subtitle="Personal and organization configuration"
        onBack={onBack}
        backLabel="Back to CrewPilot"
        headerActions={onSignOut ? <SignOutButton onClick={onSignOut} /> : null}
        layout={PAGE_LAYOUT.COMMAND}
      >
        <div className="adm-scope">
          {error ? (
            <p className="admin-placeholder-note" style={{ marginBottom: "12px" }}>
              Cloud sync unavailable. Changes are saved on this device until you
              are back online.
            </p>
          ) : null}
          <div className="adm-settings">
            <nav className="adm-settings__nav" aria-label="Settings sections">
              <div className="adm-settings__group">My Settings</div>
              {visiblePersonalSections.map((section) =>
                navButton(section, section.id === personalActive),
              )}
              {SETTINGS_CATEGORY_ORDER.map((category) => {
                const sections = groupedAdminSections.get(category);
                if (!sections || sections.length === 0) {
                  return null;
                }
                return (
                  <React.Fragment key={category}>
                    <div className="adm-settings__group">{category}</div>
                    {sections.map((section) => navButton(section, false))}
                  </React.Fragment>
                );
              })}
            </nav>
            <div className="adm-settings__body">
              <div className="rb-card user-preferences adm-settings__panel">
                <div className="section-heading">{personalMeta?.label || "Appearance"}</div>
                {personalPanel}
                {personalActive === "my_appearance" || personalActive === "my_ashley"
                  ? savedBanner
                  : null}
              </div>
            </div>
          </div>
        </div>
      </CrewPilotModuleShell>
    );
  }

  if (activeSection === "my_appearance") {
    return (
      <CrewPilotModuleShell
        title="Appearance"
        subtitle="Personal display preference"
        sectionLabel="Settings"
        onBack={handleBackToOverview}
        layout={PAGE_LAYOUT.FORM}
      >
        <div className="rb-card user-preferences">
          <AppearanceSettingsSection
            preferences={preferences}
            onUpdatePreferences={handleUpdatePreferences}
            saving={saving}
          />
          {savedBanner}
        </div>
      </CrewPilotModuleShell>
    );
  }

  if (activeSection === "my_ashley") {
    return (
      <CrewPilotModuleShell
        title="Ashley"
        subtitle="Personal voice & navigation preference"
        sectionLabel="Settings"
        onBack={handleBackToOverview}
        layout={PAGE_LAYOUT.FORM}
      >
        <div className="rb-card user-preferences">
          <AshleyVoiceSettingsSection
            preferences={preferences}
            onUpdatePreferences={handleUpdatePreferences}
            saving={saving}
          />
          {savedBanner}
        </div>
      </CrewPilotModuleShell>
    );
  }

  if (activeSection === "my_connected" && connectedServicesEnabled) {
    return (
      <CrewPilotModuleShell
        title="Connected Services"
        subtitle="Personal account connections"
        sectionLabel="Settings"
        onBack={handleBackToOverview}
        layout={PAGE_LAYOUT.FORM}
      >
        <div className="rb-card user-preferences">
          <ConnectedServicesSection />
        </div>
      </CrewPilotModuleShell>
    );
  }

  if (activeSection === "my_legal") {
    return (
      <CrewPilotModuleShell
        title="About & Legal"
        subtitle="Version and legal information"
        sectionLabel="Settings"
        onBack={handleBackToOverview}
        layout={PAGE_LAYOUT.FORM}
      >
        <div className="rb-card user-preferences">
          <AboutLegalSection />
        </div>
      </CrewPilotModuleShell>
    );
  }

  const renderAdminSection = () => {
    switch (activeSection) {
      case "organization":
        return <AdminOrganizationScreen onBack={handleBackToOverview} />;
      case "branding":
        return <AdminBrandingScreen onBack={handleBackToOverview} />;
      case "invitations":
        return <AdminInvitationsScreen onBack={handleBackToOverview} />;
      case "users":
        return (
          <AdminUsersHubScreen
            currentUser={currentUser}
            onBack={handleBackToOverview}
          />
        );
      case "trades":
        return <AdminTradesScreen onBack={handleBackToOverview} />;
      case "classifications":
        return <AdminClassificationsScreen onBack={handleBackToOverview} />;
      case "payroll_types":
        return <AdminPayrollTypesScreen onBack={handleBackToOverview} />;
      case "work_schedule":
        return <AdminWorkScheduleScreen onBack={handleBackToOverview} />;
      case "payroll_settings":
        return <AdminPayrollSettingsScreen onBack={handleBackToOverview} />;
      case "payroll":
        return <AdminPayrollRatesScreen onBack={handleBackToOverview} />;
      case "work_types":
        return <AdminWorkTypesScreen onBack={handleBackToOverview} />;
      case "invoices":
        return <AdminInvoicesSettingsScreen onBack={handleBackToOverview} />;
      case "feature_flags":
        return <AdminFeatureFlagsScreen onBack={handleBackToOverview} />;
      case "subscription":
        return <AdminSubscriptionScreen onBack={handleBackToOverview} />;
      default:
        break;
    }
    return null;
  };

  if (activeSection !== "overview" && canOpenAdminSection(activeSection)) {
    const adminScreen = renderAdminSection();
    if (adminScreen) {
      return isDesktop ? (
        <DesktopNavProvider value={settingsNav}>
          <div className="adm-scope">{adminScreen}</div>
        </DesktopNavProvider>
      ) : (
        adminScreen
      );
    }
  }

  return (
    <CrewPilotModuleShell
      title="Settings"
      subtitle="Personal and organization configuration"
      onBack={onBack}
      headerActions={onSignOut ? <SignOutButton onClick={onSignOut} /> : null}
      layout={PAGE_LAYOUT.FORM}
    >
      {error ? (
        <p className="admin-placeholder-note" style={{ marginBottom: "12px" }}>
          Cloud sync unavailable. Changes are saved on this device until you
          are back online.
        </p>
      ) : null}

      <CrewPilotNavSectionHeader>My Settings</CrewPilotNavSectionHeader>
      <CrewPilotNavList>
        {visiblePersonalSections.map((section) => (
          <CrewPilotNavRow
            key={section.id}
            icon={section.icon}
            title={section.label}
            onClick={() => handleSelectSection(section.id)}
          />
        ))}
      </CrewPilotNavList>

      {SETTINGS_CATEGORY_ORDER.map((category) => {
        const sections = groupedAdminSections.get(category);
        if (!sections || sections.length === 0) {
          return null;
        }
        return (
          <React.Fragment key={category}>
            <CrewPilotNavSectionHeader>{category}</CrewPilotNavSectionHeader>
            <CrewPilotNavList>
              {sections.map((section) => (
                <CrewPilotNavRow
                  key={section.id}
                  icon={section.icon}
                  title={section.label}
                  onClick={() => handleSelectSection(section.id)}
                />
              ))}
            </CrewPilotNavList>
          </React.Fragment>
        );
      })}
    </CrewPilotModuleShell>
  );
}
